import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import { formatDateTime, formatDuration, formatNumber, formatPercent, formatRelative } from '../format';
import {
  CALL_RESULT_LABELS,
  type CallFilterState,
  type CallRecord,
  type CallReview,
  type CallerQaResponse,
} from '../types';
import { Dialog } from './Dialog';
import { formatScore } from './LeadScoreBadge';

export const QA_PARTS: { key: string; label: string }[] = [
  { key: 'opening', label: 'Opening' },
  { key: 'discovery', label: 'Discovery' },
  { key: 'listening', label: 'Listening' },
  { key: 'pitch', label: 'Pitch' },
  { key: 'objections', label: 'Objections' },
  { key: 'close', label: 'Close / next step' },
  { key: 'professionalism', label: 'Professionalism' },
];

function band(score: number) {
  return score >= 7 ? 'high' : score >= 5 ? 'mid' : 'low';
}

/** A call QA score out of 10: the number always, colour as a second signal. */
export function QaScore({ score, title }: { score: number | null; title?: string }) {
  if (score === null || score === undefined) return <span className="muted">—</span>;
  return (
    <span className={`lead-score lead-score-${band(score)}`} title={title}>
      <span className="dot" aria-hidden="true" />
      <strong>{formatScore(score)}</strong>
      <span className="muted small">/10</span>
    </span>
  );
}

/** The call log cell: the score and result, "short", or why there is none. */
export function CallQaCell({ call, onOpen }: { call: CallRecord; onOpen: () => void }) {
  if (call.outcome !== 'answered') return <span className="muted">—</span>;
  if (call.qa?.status === 'rated') {
    return (
      <button type="button" className="link-button qa-cell" onClick={onOpen} title="Open the call review">
        <QaScore score={call.qa.score} />
        {call.qa.result ? <div className="cell-sub">{CALL_RESULT_LABELS[call.qa.result] ?? call.qa.result}</div> : null}
      </button>
    );
  }
  if (call.short) {
    return (
      <span className="short-chip" title="Answered, but too short to review (Settings → AI → Call QA)">
        Short call
      </span>
    );
  }
  return (
    <button type="button" className="link-button small muted" onClick={onOpen}>
      {call.qa?.status === 'no_transcript' ? 'No transcript yet' : 'Not reviewed'}
    </button>
  );
}

/** One call's scorecard, with a button to review it (again). */
export function CallReviewDialog({
  call,
  canReview,
  onClose,
  onReviewed,
}: {
  call: CallRecord;
  canReview: boolean;
  onClose: () => void;
  onReviewed: () => void;
}) {
  const [review, setReview] = useState<CallReview | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .callReview(call.id)
      .then((response) => setReview(response.review))
      .catch((cause) => setError(cause instanceof ApiError ? cause.message : String(cause)));
  }, [call.id]);

  async function reviewNow() {
    setBusy(true);
    setError(null);
    try {
      const response = await api.reviewCall(call.id);
      setReview(response.review);
      onReviewed();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const title = `Call review · ${call.userName ?? 'Team member'} → ${call.contactName ?? call.contactPhone ?? 'contact'}`;
  const rated = review?.status === 'rated' ? review : null;

  return (
    <Dialog title={title} onClose={onClose} wide>
      <div className="stack">
        <p className="small muted">
          {formatDateTime(call.dateAdded)} · {call.direction ?? ''} · {formatDuration(call.durationSeconds)} ·{' '}
          {call.locationName ?? call.locationId}
        </p>
        {review === undefined && !error ? <p className="empty">Loading…</p> : null}
        {review !== undefined && !rated ? (
          <p className="empty">
            {review?.status === 'no_transcript'
              ? 'HighLevel has no transcript for this call yet, so it could not be reviewed. It is tried again later.'
              : call.short
                ? 'This call is shorter than the review minimum, so it is counted as a short call instead.'
                : 'This call has not been reviewed yet. Reviews run in the background.'}
          </p>
        ) : null}
        {rated ? (
          <>
            <div className="qa-head">
              <div className="qa-overall">
                <span className="stat-label">Overall</span>
                <QaScore score={rated.score} />
              </div>
              <div>
                {rated.result ? (
                  <span className="role-chip">{CALL_RESULT_LABELS[rated.result] ?? rated.result}</span>
                ) : null}
                {rated.leadInterest ? <span className="small muted"> · lead interest {rated.leadInterest}</span> : null}
                {rated.summary ? <p style={{ margin: '6px 0 0' }}>{rated.summary}</p> : null}
              </div>
            </div>
            <div className="qa-parts">
              {QA_PARTS.map((part) => {
                const value = rated.scores[part.key];
                return (
                  <div className="qa-part" key={part.key}>
                    <span className="small">{part.label}</span>
                    <div className="qa-bar" aria-hidden="true">
                      {typeof value === 'number' ? (
                        <span className={`qa-fill qa-${band(value)}`} style={{ width: `${value * 10}%` }} />
                      ) : null}
                    </div>
                    <span className="small num">{typeof value === 'number' ? formatScore(value) : 'n/a'}</span>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-2">
              <div>
                <h3 className="small-heading">Went well</h3>
                <ul className="tight-list">
                  {rated.strengths.length ? (
                    rated.strengths.map((item) => <li key={item}>{item}</li>)
                  ) : (
                    <li className="muted">—</li>
                  )}
                </ul>
              </div>
              <div>
                <h3 className="small-heading">To improve</h3>
                <ul className="tight-list">
                  {rated.improvements.length ? (
                    rated.improvements.map((item) => <li key={item}>{item}</li>)
                  ) : (
                    <li className="muted">—</li>
                  )}
                </ul>
              </div>
            </div>
            {rated.coachingTip ? (
              <div className="inline-result">
                <strong>Coaching tip:</strong> {rated.coachingTip}
              </div>
            ) : null}
            <span className="small muted">
              <span className="ai-spark">✦</span> Reviewed by AI {formatRelative(rated.reviewedAt)} from the HighLevel
              transcript
              {rated.model ? ` (${rated.model})` : ''}.
            </span>
          </>
        ) : null}
        {error ? <div className="inline-result bad">{error}</div> : null}
        {canReview && review !== undefined ? (
          <div className="row">
            <button type="button" className="btn btn-sm" disabled={busy} onClick={reviewNow}>
              {busy ? 'Reviewing…' : rated ? 'Review again' : 'Review now'}
            </button>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * The Calls tab's team scorecard: per team member, the calls they made, how
 * many were too short to judge, and how the reviewed ones scored.
 */
export function CallerQaCard({
  data,
  filters,
  onFiltersChange,
  onOpenCalls,
}: {
  data: CallerQaResponse | null;
  filters: CallFilterState;
  onFiltersChange: (next: CallFilterState) => void;
  onOpenCalls: () => void;
}) {
  const [sort, setSort] = useState<'answered' | 'score' | 'short'>('answered');
  const rows = useMemo(() => {
    const list = (data?.byUser ?? []).filter((row) => row.userId && row.answered > 0);
    const names = new Map<string, number>();
    for (const row of list) names.set(row.userName ?? row.userId!, (names.get(row.userName ?? row.userId!) ?? 0) + 1);
    return list
      .map((row) => ({
        ...row,
        label: row.userName ?? row.userId!,
        repeated: (names.get(row.userName ?? row.userId!) ?? 0) > 1,
      }))
      .sort((a, b) =>
        sort === 'score'
          ? (b.averageScore ?? -1) - (a.averageScore ?? -1)
          : sort === 'short'
            ? b.short / Math.max(b.answered, 1) - a.short / Math.max(a.answered, 1)
            : b.answered - a.answered,
      );
  }, [data, sort]);

  if (!data) return null;
  const { totals } = data;
  const open = (userId: string, qa: CallFilterState['qa'] = '') => {
    onFiltersChange({ ...filters, userIds: [userId], qa });
    onOpenCalls();
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>
          Call QA by team member <span className="ai-spark">✦</span>
        </h2>
        <div className="segmented" role="group" aria-label="Sort">
          {(
            [
              ['answered', 'Most calls'],
              ['score', 'Best score'],
              ['short', 'Most short calls'],
            ] as const
          ).map(([value, label]) => (
            <button type="button" key={value} aria-pressed={sort === value} onClick={() => setSort(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="card-sub">
        Each answered call of {data.minSeconds}s or more is reviewed by AI from its transcript and scored 0–10 on how
        the team member handled it. Shorter answered calls are counted as <strong>short calls</strong> and not reviewed.
        So far {formatNumber(totals.reviewed)} of {formatNumber(totals.eligible)} reviewable calls in this range are
        reviewed
        {totals.averageScore !== null ? `, averaging ${formatScore(totals.averageScore)}/10` : ''}.
        {!data.enabled ? ' Call QA is off (Settings → AI).' : ''} Click a number to open those calls.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Team member</th>
              <th className="num">Calls</th>
              <th className="num">Answered</th>
              <th className="num" title={`Answered calls under ${data.minSeconds}s`}>
                Short calls
              </th>
              <th className="num">Reviewed</th>
              <th className="num">Avg score</th>
              {QA_PARTS.map((part) => (
                <th className="num" key={part.key}>
                  {part.label.replace(' / next step', '')}
                </th>
              ))}
              <th className="num" title="Reviewed calls scored under 5">
                Under 5
              </th>
              <th className="num" title="Appointments set and live transfers, as the reviews found them">
                Appts / transfers
              </th>
              <th className="num">Talk time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.userId}-${row.locationId}`}>
                <td className="nowrap">
                  <span className="cell-strong">{row.label}</span>
                  {row.repeated || filters.locationIds.length !== 1 ? (
                    <div className="cell-sub">{row.locationName}</div>
                  ) : null}
                </td>
                <td className="num">{formatNumber(row.calls)}</td>
                <td className="num">{formatNumber(row.answered)}</td>
                <td className="num">
                  <button type="button" className="link-button" onClick={() => open(row.userId!, 'short')}>
                    {formatNumber(row.short)}
                  </button>
                  <div className="cell-sub">{formatPercent(row.answered ? row.short / row.answered : 0)}</div>
                </td>
                <td className="num">
                  <button type="button" className="link-button" onClick={() => open(row.userId!, 'reviewed')}>
                    {formatNumber(row.reviewed)}
                  </button>
                </td>
                <td className="num">
                  <QaScore score={row.averageScore} />
                </td>
                {QA_PARTS.map((part) => (
                  <td className="num" key={part.key}>
                    {typeof row.criteria[part.key] === 'number' ? formatScore(row.criteria[part.key]!) : '—'}
                  </td>
                ))}
                <td className="num">
                  {row.low ? (
                    <button type="button" className="link-button" onClick={() => open(row.userId!, 'low')}>
                      {formatNumber(row.low)}
                    </button>
                  ) : (
                    '0'
                  )}
                </td>
                <td className="num">
                  {formatNumber(row.appointments)} / {formatNumber(row.transferred)}
                </td>
                <td className="num">{formatDuration(row.talkSeconds)}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9 + QA_PARTS.length} className="empty">
                  No answered calls by a team member in this range.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
